<script lang="ts" setup>
import type { ComponentPublicInstance } from 'vue'
import type { ContentFlashcard } from '~/models/Content/ContentFlashcards'
import type { BaseItem } from '~/models/Content/BaseItem'
import type { TTSLanguage } from '~/models/Brikke/TextToSpeech'
import { useI18n } from 'vue-i18n'
import { computed, ref, useTemplateRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { vIntersectionObserver } from '@vueuse/components'
import { faPause, faPlay } from '@fortawesome/pro-solid-svg-icons'
import { faArrowLeft, faArrowRight, faRotateLeft, faSpinner } from '@fortawesome/duotone-regular-svg-icons'
import { setTitle } from '~/utils/dom'
import useLicenseControlStore from '~/stores/licenseControl'
import useAuthStore from '~/stores/auth'
import { PendoTrackName } from '~/models/Pendo'
import { ContentType } from '~/models/Content/ContentType'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import usePendo from '~/composables/usePendo'
import { useOverlayOutlet } from '~/composables/useOverlayOutlet'
import useLanguageDetector from '~/composables/useLanguageDetector'
import { useFlashcardsQuery } from '~/composables/useFlashcardsQuery'
import { useContentEvent } from '~/composables/activity/useContentEvent'
import FlashcardAnimation from '~/assets/lottie/Flashcard_Complete.json'
import TopBar from '~/components/utils/TopBar.vue'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import CopyrightButton from '~/components/utils/CopyrightButton.vue'
import Button from '~/components/ui/Button.vue'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import TextToSpeechButton from '~/components/media/TextToSpeechButton.vue'
import Image from '~/components/media/Image.vue'
import AudioPlayer from '~/components/media/AudioPlayer.vue'
import FlashCard from '~/components/flashcards/FlashCard.vue'

const props = defineProps<{ locationId: number }>()

const { t } = useI18n()
const startButton = useTemplateRef<ComponentPublicInstance>('start')
const { buildAudioUrl } = useUrlBuilder()
const { checkItemLicense } = useLicenseControlStore()
const { pendoTrack, pendoFlushNow } = usePendo()
const { isAuthenticated } = storeToRefs(useAuthStore())

const scroller = ref<HTMLDivElement | null>(null)
const isFullscreen = ref(false)

const overlayOutlet = useOverlayOutlet(useTemplateRef('overlay-outlet'))

const { data, isLoading } = useFlashcardsQuery(props.locationId)

const shuffledCards = ref(sortCards(data.value?.cards ?? []))

const detectLanguageFront = computed(() => shuffledCards.value
  .filter(card => card.languageDetection && card.bodyFront)
  .map(card => ({
    contentId: card.contentId,
    text: [card.bodyFront ?? ''],
  }))
)

const detectLanguageBack = computed(() => shuffledCards.value
  .filter(card => card.languageDetection && card.bodyBack)
  .map(card => ({
    contentId: card.contentId,
    text: [card.bodyBack ?? ''],
  }))
)

const { result: detectedLanguagesFront, isLoading: isDetectingLangFront } = useLanguageDetector(detectLanguageFront)
const { result: detectedLanguagesBack, isLoading: isDetectingLangBack } = useLanguageDetector(detectLanguageBack)

const getEffectiveTTSLanguage = (card: ContentFlashcard, side: 'front' | 'back'): TTSLanguage | null => {
  const detectedLanguages = side === 'front' ? detectedLanguagesFront : detectedLanguagesBack

  if (card.languageDetection && detectedLanguages.value.has(card.contentId)) {
    const detected = detectedLanguages.value.get(card.contentId)
    if (detected) {
      return detected
    }
  }

  return card.textToSpeechLanguage?.identifier || null
}
const activeCardIndex = ref(-1)
const isLast = ref(true)
const isRevealed = ref(false)

const location = computed(() => data.value?.location)

const { autoContentEvent } = useContentEvent()
autoContentEvent(location)

watch(location, () => {
  if (!location.value)
    return
  setTitle(location.value.title)
  checkItemLicense(location.value)
}, { immediate: true })

watch(() => data.value?.cards, (newCards, oldCards) => {
  if (newCards && (oldCards?.length !== newCards?.length)) {
    shuffledCards.value = sortCards(newCards)
  }
})

enum Dir {
  Forwards = 1,
  Backwards = -1,
}

function scroll(dir: Dir) {
  const el = scroller.value
  if (!el)
    return

  el.scrollTo({
    left: el.scrollLeft + dir * el.clientWidth,
    behavior: 'smooth',
  })
}

function reload() {
  scroller.value?.scrollTo({ left: 0 })
  shuffledCards.value = sortCards(shuffledCards.value)
  startButton.value?.$el?.focus?.()
}

function sortCards<T extends BaseItem>(cards: T[]): T[] {
  const newCards = [...cards]
  if (data?.value?.location?.sorting === 'priority') {
    const priority = data.value.location.cards?.destinationContentIds ?? []
    return newCards.sort((a, b) => priority.indexOf(a.contentId) - priority.indexOf(b.contentId))
  }
  const random = Array.from({ length: newCards.length }, Math.random)
  return newCards.sort((a, b) => random[newCards.indexOf(a)] > random[newCards.indexOf(b)] ? 1 : -1)
}

function updateInView(index: number, inView: boolean) {
  if (inView)
    activeCardIndex.value = index
}

function updateInViewStartObserver([entry]: IntersectionObserverEntry[]) {
  if (entry.isIntersecting)
    activeCardIndex.value = -1
}

function updateInViewEndObserver([entry]: IntersectionObserverEntry[]) {
  isLast.value = !entry.isIntersecting
  if (isLast.value)
    return
  pendoTrack(PendoTrackName.FlashcardComplete, {
    title: document.title,
    url: window.location.href
  })
  pendoFlushNow()
}
</script>

<template>
  <TopBar
    :color-pair="location?.colorPair"
    :item="location"
    variant="colorPair"
    @fullscreen="isFullscreen = $event"
  />

  <div
    v-if="isLoading"
    class="h-full"
  >
    <AuSkeletonWrapper class="grid h-full place-content-center place-items-center gap-4">
      <AuSkeleton
        height="4rem"
        width="30ch"
      />
      <AuSkeleton
        height="2rem"
        width="45ch"
      />
      <AuSkeleton
        class="!rounded-full"
        height="3rem"
        width="20ch"
      />
    </AuSkeletonWrapper>
  </div>

  <div
    v-else-if="location"
    ref="scroller"
    :style="{
      'backgroundColor': location.colorPair.background,
      'color': location.colorPair.text,
      'scrollbarWidth': 'none',
      '--media-bg-color': location.colorPair.background,
      '--media-color': location.colorPair.text,
      '--card-width': isFullscreen ? '100dvw' : '80dvw',
      '--card-height': isFullscreen ? '100dvh' : '80dvh',
      '--card-spacing': isFullscreen ? '0' : '3rem',
    }"
    class="relative flex h-full snap-x snap-mandatory items-center gap-(--card-spacing) overflow-x-scroll scroll-smooth px-[calc(10vh+var(--card-spacing))]"
    tabindex="0"
    @keydown.left.prevent="scroll(Dir.Backwards)"
    @keydown.right.prevent="scroll(Dir.Forwards)"
  >
    <div
      v-intersection-observer="[updateInViewStartObserver, { threshold: .5 }]"
      class="grid h-(--card-height) w-(--card-width) flex-none snap-center snap-always place-content-center place-items-center gap-8"
    >
      <h1 class="text-5xl">
        {{ location.title }}
      </h1>
      <p class="text-xl">
        {{ t('flashcards.tip') }}
      </p>
      <Button
        ref="start"
        :color="location.colorPair.background"
        size="xl"
        :disabled="!isAuthenticated"
        @click="scroll(Dir.Forwards)"
      >
        {{ t('flashcards.start') }}
        <font-awesome-icon :icon="faArrowRight" />
      </Button>
    </div>

    <FlashCard
      v-for="({ card, image, audio }, index) of shuffledCards.map((card) => ({
        card,
        image: (card.imageFront && data?.images && data.images.get(Number(card.imageFront.destinationContentId))!) || undefined,
        audio: (card.audioFront && data?.audioFiles && data.audioFiles.get(Number(card.audioFront.destinationContentId))!) || undefined,
      }))"
      :key="card.locationId"
      :class="[location.textSizes.heading, `flashcard-${card.locationId}`]"
      :style="{
        'backgroundColor': location.colorPair.text,
        'color': location.colorPair.background,
        '--flashcard-safe-inset': isFullscreen ? '4rem' : '0',
      }"
      class="relative grid h-(--card-height) w-(--card-width) flex-none snap-center snap-always place-items-center overflow-hidden rounded-lg bg-white"
      @view="updateInView(index, $event)"
      @reveal="isRevealed = $event"
    >
      <template #front>
        <Image
          v-if="image"
          :class="image.contentTypeIdentifier === ContentType.Svg ? 'object-contain' : 'object-cover'"
          :content="image"
          class="max-h-full min-h-0 !w-full min-w-0 place-self-stretch"
        />
        <div
          :class="{ 'sr-only': image }"
          class="max-w-[40ch] p-24"
          data-key-tts="front"
        >
          <RichTextRenderer :text="card.bodyFront ?? 'no text'" />
        </div>
        <CopyrightButton
          v-if="image && 'copyright' in image"
          :copyright="image.copyright"
          class="!absolute right-8 bottom-8 !w-auto text-lg"
          @keydown.enter.stop
          @keydown.space.stop
          @click.stop
        />
      </template>

      <template #back>
        <div class="max-w-[40ch] p-24" data-key-tts="back">
          <RichTextRenderer :text="card.bodyBack ?? 'no text'" />
        </div>
      </template>

      <template
        v-if="audio"
        #overlay
      >
        <AudioPlayer
          :media-id="audio.metadata?.contentItemId"
          :src="{
            src: buildAudioUrl(audio),
            type: audio.metadata?.mimeType ?? audio.file?.mimeType,
          }"
          class="-translate-x-1/2 !rounded-full"
        >
          <template #controls="{ isPlaying }">
            <media-play-button
              class="group grid size-32 cursor-pointer place-content-center rounded-full bg-(--media-bg-color) text-3xl text-(--media-color) shadow-lg hover:not-disabled:text-[rgb(from_var(--media-color)_r_g_b_/0.8)]"
              tabindex="-1"
            >
              <font-awesome-icon
                :class="{ 'translate-x-[5%]': !isPlaying }"
                :icon="isPlaying ? faPause : faPlay"
                size="xl"
              />
            </media-play-button>
          </template>
        </AudioPlayer>
      </template>
      <template v-else-if="card.textToSpeech && ((isRevealed && card.bodyBack) || (!isRevealed && card.bodyFront && !image))" #overlay>
        <TextToSpeechButton
          v-if="getEffectiveTTSLanguage(card, isRevealed ? 'back' : 'front')"
          :item="card"
          :language-code="getEffectiveTTSLanguage(card, isRevealed ? 'back' : 'front')!"
          :selector="`.flashcard-${card.locationId}`"
        >
          <template #trigger="{ activate, isPlaying, isActive, pause, generate, isLoadingTts }">
            <Button
              class="group grid size-32 -translate-x-1/2 cursor-pointer place-content-center rounded-full bg-(--media-bg-color) text-3xl text-(--media-color) shadow-lg hover:not-disabled:text-[rgb(from_var(--media-color)_r_g_b_/0.8)]"
              shape="square"
              size="xl"
              :disabled="isLoadingTts || isDetectingLangFront || isDetectingLangBack"
              @click="isPlaying ? pause() : isActive ? generate() : activate()"
            >
              <font-awesome-icon
                :class="{ 'translate-x-[5%]': !isPlaying }"
                :icon="isLoadingTts ? faSpinner : isPlaying ? faPause : faPlay"
                size="xl"
                :spin="isLoadingTts"
              />
            </Button>
          </template>
        </TextToSpeechButton>
      </template>
    </FlashCard>

    <div
      v-intersection-observer="[updateInViewEndObserver, { threshold: .5 }]"
      class="flex h-(--card-height) w-(--card-width) flex-none snap-center snap-always flex-col place-content-center place-items-center bg-center bg-no-repeat md:flex-row"
    >
      <LottieAnimation
        :animation-data="FlashcardAnimation"
        class="self-end"
        height="500px"
        width="250px"
      />
      <div class="flex flex-col items-center gap-8">
        <p class="fluid-text-4xl text-center">
          {{ t('flashcards.complete') }}
        </p>
        <Button
          :color="location.colorPair.background"
          size="xl"
          @click="reload()"
        >
          <font-awesome-icon :icon="faRotateLeft" />
          {{ t('flashcards.retry') }}
        </Button>
      </div>
      <LottieAnimation
        :animation-data="FlashcardAnimation"
        class="rotate-180 self-start"
        height="500px"
        width="250px"
      />
    </div>

    <nav
      :class="{ 'pointer-events-none opacity-0': activeCardIndex < 0 }"
      class="fixed bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-6 transition-opacity"
    >
      <Button
        :color="location.colorPair.background"
        shape="square"
        size="xl"
        @click="scroll(Dir.Backwards)"
      >
        <font-awesome-icon :icon="faArrowLeft" />
      </Button>
      <div
        :style="{ background: location.colorPair.background }"
        class="text-md md:fluid-text-xl flex items-center self-stretch rounded px-2 tabular-nums"
      >
        {{ t('flashcards.stage', {
          n: Math.max(1, activeCardIndex + 1),
          total: shuffledCards?.length ?? 0,
        }) }}
      </div>
      <Button
        :class="{ 'pointer-events-none invisible opacity-0': !isLast }"
        :color="location.colorPair.background"
        :disabled="!isLast"
        shape="square"
        size="xl"
        @click="scroll(Dir.Forwards)"
      >
        <font-awesome-icon :icon="faArrowRight" />
      </Button>
    </nav>
  </div>

  <div
    v-else
    class="grid h-full place-content-center p-6"
  >
    {{ t('flashcards.error') }}
  </div>

  <div
    v-if="data"
    :id="overlayOutlet.id"
    ref="overlay-outlet"
    class="pointer-events-auto fixed bottom-0 z-[100] w-full bg-(--media-bg-color) text-(--media-color) [&:not(:empty)]:p-4"
    :style="{
      '--media-color': data.location.colorPair.background,
      '--media-bg-color': data.location.colorPair.text,
    }"
  />
</template>
